import { supabase } from '../../shared/supabase/supabaseClient';
import type { BusinessTypeCode } from '../../shell/session/domain/businessProfile';
import { clearBootstrapSessionCache, fetchBootstrapMobileSession } from '../../shell/session/bootstrapMobileSessionService';
import { hydrateSessionCachesFromBootstrap } from '../../shell/session/hydrateSessionCachesFromBootstrap';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { saveOrganizationBusinessProfileOffline } from '../../shell/connectivity/businessProfileCache';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { clearLocalCatalogDataForBusinessTypeChange } from '../../shared/localDb';
import { resetCatalogReadPreflightState } from '../../catalog/infra-api'
import { resetCatalogSnapshotSyncRuntimeState } from '../../catalog/infra-api';
import {
  mapChangeBusinessTypeError,
  parseBusinessTypeChangeBlockers,
  parseChangeBusinessTypeResult,
  type BusinessTypeChangeBlockers,
  type ChangeOrganizationBusinessTypeResult,
} from './businessTypeChangeUtils';

export type ChangeOrganizationBusinessTypeParams = {
  newBusinessTypeCode: BusinessTypeCode;
  resetCatalog?: boolean;
  applyNewPack?: boolean;
  syncBranchDefaults?: boolean;
  requestedVerticalLabel?: string | null;
  updateRequestedVerticalLabel?: boolean;
};

export async function changeOrganizationBusinessType(
  params: ChangeOrganizationBusinessTypeParams,
): Promise<ChangeOrganizationBusinessTypeResult> {
  const { data, error } = await supabase.rpc('change_organization_business_type', {
    p_new_business_type_code: params.newBusinessTypeCode,
    p_reset_catalog: params.resetCatalog ?? true,
    p_apply_new_pack: params.applyNewPack ?? false,
    p_sync_branch_defaults: params.syncBranchDefaults ?? true,
    p_requested_vertical_label: params.requestedVerticalLabel ?? null,
    p_update_requested_vertical_label: params.updateRequestedVerticalLabel ?? false,
  });

  if (error) {
    throw new Error(mapChangeBusinessTypeError(error.message));
  }

  const parsed = parseChangeBusinessTypeResult(data);
  if (!parsed) {
    throw new Error('No se pudo interpretar la respuesta del servidor.');
  }

  useBusinessProfileStore.getState().setProfile(parsed.businessProfile);
  try {
    await saveOrganizationBusinessProfileOffline(parsed.businessProfile);
  } catch (cacheErr) {
    console.warn('[changeOrganizationBusinessType] offline profile save failed', cacheErr);
  }

  const userId = useAuthStore.getState().session?.user?.id;
  if (userId) {
    clearBootstrapSessionCache();
    await fetchBootstrapMobileSession(userId, { force: true });
    await hydrateSessionCachesFromBootstrap(userId);
  }

  try {
    await clearLocalCatalogDataForBusinessTypeChange();
    resetCatalogSnapshotSyncRuntimeState();
    resetCatalogReadPreflightState();
  } catch (localErr) {
    console.warn('[changeOrganizationBusinessType] local catalog reset failed', localErr);
  }

  await logBusinessAuditEvent('analytics.business_type_changed', {
    from_code: parsed.fromBusinessTypeCode,
    to_code: parsed.toBusinessTypeCode,
    reset_catalog: params.resetCatalog ?? true,
    pack_applied: parsed.packApplied,
  });

  return parsed;
}

export async function fetchBusinessTypeChangeBlockers(): Promise<BusinessTypeChangeBlockers> {
  const { data, error } = await supabase.rpc('get_business_type_change_blockers');
  if (error) {
    throw new Error(mapChangeBusinessTypeError(error.message));
  }
  const parsed = parseBusinessTypeChangeBlockers(data);
  if (!parsed) {
    throw new Error('No se pudo chequear si hay caja, pedidos o turnos abiertos.');
  }
  return parsed;
}
